import type { PlotTrace } from "./manifest.ts";
import { logBinomialMass, logPoissonMass, sampleCounts, type LogConcaveCounts } from "./probability.ts";
import { seededUniform } from "./random.ts";

/**
 * The five parameterized teaching models: their parameters and domains, formula curves and seeded
 * samples. The server compiles a model chart with them and the chat renderer's model controls
 * recompute it with them, so both draw the same model.
 */

export const DISTRIBUTIONS = ["normal", "uniform", "exponential", "binomial", "poisson"] as const;
export type Distribution = (typeof DISTRIBUTIONS)[number];

export interface ModelParameter {
	name: string;
	/** The control's label. */
	label: string;
	domain: string;
	integer?: boolean;
}

export interface ModelDescription {
	distribution: Distribution;
	parameters: ModelParameter[];
	support: string;
	curve: string;
	sampler: string;
	discrete: boolean;
}

/** The largest Poisson rate: every count it plausibly gives is an exact integer in double precision. */
export const MAX_POISSON_RATE = 1e15;

const COUNT_CURVE =
	"at every integer from the mode - 8 SD to the mode + 8 SD (at least the mode ± 3) within the support; past 1001 integers, at every s-th integer counted from the mode, s the smallest odd step that keeps 1001 or fewer";

export const MODELS: readonly ModelDescription[] = [
	{
		distribution: "normal",
		parameters: [
			{ name: "mean", label: "Mean", domain: "finite number" },
			{ name: "variance", label: "Variance", domain: "finite number > 0" },
		],
		support: "all real numbers",
		curve: "density exp(-(x - mean)^2 / (2 variance)) / sqrt(2 pi variance) at 201 evenly spaced points over mean ± 4 standard deviations",
		sampler: "Box-Muller: mean + sqrt(variance) sqrt(-2 ln(1 - u1)) cos(2 pi u2)",
		discrete: false,
	},
	{
		distribution: "uniform",
		parameters: [
			{ name: "min", label: "Minimum", domain: "finite number" },
			{ name: "max", label: "Maximum", domain: "finite number > min" },
		],
		support: "[min, max]",
		curve: "density 1 / (max - min) at 101 evenly spaced points on the closed interval [min, max], and 0 at points a millionth and a tenth of the width beyond each end",
		sampler: "min + (max - min) u",
		discrete: false,
	},
	{
		distribution: "exponential",
		parameters: [{ name: "rate", label: "Rate", domain: "finite number > 0" }],
		support: "x >= 0",
		curve: "density rate exp(-rate x) at 201 evenly spaced points from 0 to 8 / rate, and 0 at points a millionth of 8 / rate and 0.4 / rate below 0",
		sampler: "inversion: -ln(1 - u) / rate",
		discrete: false,
	},
	{
		distribution: "binomial",
		parameters: [
			{ name: "trials", label: "Trials", domain: "integer from 1 to 9007199254740991 (2^53 - 1)", integer: true },
			{ name: "probability", label: "Probability", domain: "finite number in [0, 1]" },
		],
		support: "integers 0..trials",
		curve: `mass C(trials, k) probability^k (1 - probability)^(trials - k) by Loader's saddle-point formula, ${COUNT_CURVE}`,
		sampler: "exact rejection from a flat-and-geometric hat over the log-concave mass, about 1.3 tries per draw for any parameters",
		discrete: true,
	},
	{
		distribution: "poisson",
		parameters: [{ name: "rate", label: "Rate", domain: `finite number > 0, at most ${MAX_POISSON_RATE} so every count is an exact integer` }],
		support: "integers >= 0",
		curve: `mass exp(-rate) rate^k / k! by Loader's saddle-point formula, ${COUNT_CURVE}`,
		sampler: "exact rejection from a flat-and-geometric hat over the log-concave mass, about 1.3 tries per draw for any parameters",
		discrete: true,
	},
];

/** A model's sample: how many observations, and the seed that reproduces them. */
export const SAMPLE_SIZE = { min: 1, max: 100_000 } as const;

export const SAMPLE_RULE =
	"The sample is drawn from uniforms u in [0, 1) of a xoshiro128** generator seeded by splitmix32 from the seed, so the same distribution, parameters, sample size and seed give the same sample. It is drawn as a density histogram: for a count model one bin per plotted step of the mass curve; otherwise min(100, ceil(sqrt(n))) equal bins from the support's lower bound (the sample's minimum when unbounded) to its upper bound (half a bin past the sample's maximum when unbounded).";

/** A validated teaching model with its formula curve and seeded sample. */
export interface TeachingModel {
	distribution: Distribution;
	parameters: Record<string, number>;
	sampleSize: number;
	seed: number;
	sample: number[];
	curve: { x: number[]; y: number[] };
}

export type ModelErrorCode = "INVALID_MODEL" | "UNSUPPORTED_MODEL";

export type ModelResult = { ok: true; model: TeachingModel } | { ok: false; code: ModelErrorCode; message: string };

class ModelError extends Error {
	readonly code: ModelErrorCode;

	constructor(code: ModelErrorCode, message: string) {
		super(message);
		this.code = code;
	}
}

type Params = Record<string, number>;

interface Family {
	/** Refuses parameters outside the domain, given that each is a finite number. */
	check(p: Params): void;
	/** The curve's x values, ascending. */
	grid(p: Params): number[];
	/** The density or mass at x. */
	at(p: Params, x: number): number;
	sample(p: Params, uniform: () => number, size: number): number[];
	/** The bins a sample is drawn in. */
	bins(p: Params, sample: number[]): HistogramBins;
}

export interface HistogramBins {
	start: number;
	end: number;
	size: number;
}

const FAMILIES: Record<Distribution, Family> = {
	normal: {
		check: (p) => {
			if (!(p.variance! > 0)) refuse(`the normal variance must be above 0, not ${p.variance}`);
		},
		grid: (p) => {
			const sd = Math.sqrt(p.variance!);
			return Array.from({ length: 201 }, (_, j) => p.mean! + sd * (-4 + (8 * j) / 200));
		},
		at: (p, x) => {
			const sd = Math.sqrt(p.variance!);
			const z = (x - p.mean!) / sd;
			return Math.exp(-0.5 * z * z) / (sd * Math.sqrt(2 * Math.PI));
		},
		sample: (p, uniform, size) => {
			const sd = Math.sqrt(p.variance!);
			return Array.from({ length: size }, () => p.mean! + sd * Math.sqrt(-2 * Math.log(1 - uniform())) * Math.cos(2 * Math.PI * uniform()));
		},
		bins: (p, sample) => continuousBins(sample, Math.sqrt(p.variance!) / 2),
	},
	uniform: {
		check: (p) => {
			if (!(p.max! > p.min!)) refuse(`the uniform max must be above min; got min ${p.min} and max ${p.max}`);
		},
		grid: (p) => {
			const width = p.max! - p.min!;
			const inside = Array.from({ length: 101 }, (_, j) => (j === 100 ? p.max! : p.min! + (width * j) / 100));
			return [p.min! - width / 10, p.min! - width * 1e-6, ...inside, p.max! + width * 1e-6, p.max! + width / 10];
		},
		at: (p, x) => (x >= p.min! && x <= p.max! ? 1 / (p.max! - p.min!) : 0),
		sample: (p, uniform, size) => Array.from({ length: size }, () => Math.min(p.max!, p.min! + (p.max! - p.min!) * uniform())),
		bins: (p, sample) => continuousBins(sample, (p.max! - p.min!) / 10, p.min!, p.max!),
	},
	exponential: {
		check: (p) => {
			if (!(p.rate! > 0)) refuse(`the exponential rate must be above 0, not ${p.rate}`);
		},
		grid: (p) => {
			const end = 8 / p.rate!;
			return [-end / 20, -end * 1e-6, ...Array.from({ length: 201 }, (_, j) => (end * j) / 200)];
		},
		at: (p, x) => (x >= 0 ? p.rate! * Math.exp(-p.rate! * x) : 0),
		sample: (p, uniform, size) => Array.from({ length: size }, () => -Math.log1p(-uniform()) / p.rate!),
		bins: (p, sample) => continuousBins(sample, 0.1 / p.rate!, 0),
	},
	binomial: {
		check: (p) => {
			if (!Number.isSafeInteger(p.trials) || p.trials! < 1) refuse(`binomial trials must be an integer from 1 to ${Number.MAX_SAFE_INTEGER}, not ${p.trials}`);
			if (!(p.probability! >= 0 && p.probability! <= 1)) refuse(`the binomial probability must be in [0, 1], not ${p.probability}`);
		},
		grid: (p) => countGrid(binomialCounts(p)),
		at: (p, k) => Math.exp(logBinomialMass(k, p.trials!, p.probability!)),
		sample: (p, uniform, size) => {
			if (p.probability === 0) return new Array<number>(size).fill(0);
			if (p.probability === 1) return new Array<number>(size).fill(p.trials!);
			return sampleCounts(binomialCounts(p), uniform, size);
		},
		bins: (p, sample) => countBins(binomialCounts(p), sample),
	},
	poisson: {
		check: (p) => {
			if (!(p.rate! > 0 && p.rate! <= MAX_POISSON_RATE)) refuse(`the Poisson rate must be above 0 and at most ${MAX_POISSON_RATE}, not ${p.rate}`);
		},
		grid: (p) => countGrid(poissonCounts(p)),
		at: (p, k) => Math.exp(logPoissonMass(k, p.rate!)),
		sample: (p, uniform, size) => sampleCounts(poissonCounts(p), uniform, size),
		bins: (p, sample) => countBins(poissonCounts(p), sample),
	},
};

function binomialCounts(p: Params): LogConcaveCounts {
	const n = p.trials!;
	const prob = p.probability!;
	return {
		lo: 0,
		hi: n,
		mode: Math.min(n, Math.floor((n + 1) * prob)),
		sd: Math.sqrt(n * prob * (1 - prob)),
		logMass: (k) => logBinomialMass(k, n, prob),
	};
}

function poissonCounts(p: Params): LogConcaveCounts {
	const rate = p.rate!;
	return {
		lo: 0,
		hi: Infinity,
		mode: Math.floor(rate),
		sd: Math.sqrt(rate),
		logMass: (k) => logPoissonMass(k, rate),
	};
}

/** Most points a count model's curve has. */
const COUNT_POINTS = 1001;

/** The counts a mass curve is drawn at, and the odd step between them. */
function countSteps(d: LogConcaveCounts): { lo: number; hi: number; step: number } {
	const lo = Math.max(d.lo, Math.min(Math.floor(d.mode - 8 * d.sd), d.mode - 3));
	const hi = Math.min(d.hi, Math.max(Math.ceil(d.mode + 8 * d.sd), d.mode + 3));
	const span = hi - lo + 1;
	const step = span <= COUNT_POINTS ? 1 : 2 * Math.ceil((span / COUNT_POINTS - 1) / 2) + 1;
	return { lo, hi, step };
}

function countGrid(d: LogConcaveCounts): number[] {
	const { lo, hi, step } = countSteps(d);
	const first = d.mode - Math.floor((d.mode - lo) / step) * step;
	const out: number[] = [];
	for (let k = first; k <= hi; k += step) out.push(k);
	return out;
}

/** Bins one curve step wide, centered on the steps, from the lowest to the highest sampled count. */
function countBins(d: LogConcaveCounts, sample: number[]): HistogramBins {
	const { step } = countSteps(d);
	const [min, max] = extent(sample);
	const bin = (k: number) => Math.floor((k - d.mode + step / 2) / step);
	return { start: d.mode - step / 2 + bin(min) * step, end: d.mode + step / 2 + bin(max) * step, size: step };
}

/**
 * min(100, ceil(sqrt(n))) equal bins from the support's lower bound to its upper, or to half a bin
 * past the sample where unbounded; `fallback` wide when the sample has no spread to divide.
 */
function continuousBins(sample: number[], fallback: number, lower?: number, upper?: number): HistogramBins {
	const [min, max] = extent(sample);
	const count = Math.min(100, Math.ceil(Math.sqrt(sample.length)));
	const start = lower ?? min;
	let size = upper === undefined ? (max - start) / (count - 0.5) : (upper - start) / count;
	if (!(size > 0)) size = fallback;
	let end = start + count * size;
	while (end <= max) end += size;
	return { start, end, size };
}

function extent(values: number[]): [number, number] {
	let lo = Infinity;
	let hi = -Infinity;
	for (const v of values) {
		if (v < lo) lo = v;
		if (v > hi) hi = v;
	}
	return [lo, hi];
}

function refuse(message: string): never {
	throw new ModelError("INVALID_MODEL", message);
}

/**
 * A model request `{ distribution, parameters, sampleSize, seed }` validated and calculated: the
 * curve from the formula and the sample from the seed. An unknown distribution is UNSUPPORTED_MODEL;
 * anything else outside its domain, INVALID_MODEL.
 */
export function teachingModel(request: unknown): ModelResult {
	try {
		return { ok: true, model: calculate(request) };
	} catch (err) {
		if (err instanceof ModelError) return { ok: false, code: err.code, message: err.message };
		throw err;
	}
}

function calculate(request: unknown): TeachingModel {
	if (!isObject(request)) refuse("model must be { distribution, parameters, sampleSize, seed }");
	const unknown = Object.keys(request).filter((key) => !["distribution", "parameters", "sampleSize", "seed"].includes(key));
	if (unknown.length) refuse(`model has unknown field "${unknown[0]}"; it takes distribution, parameters, sampleSize and seed`);
	const { distribution, sampleSize, seed } = request;
	if (typeof distribution !== "string") refuse(`model.distribution must be one of ${DISTRIBUTIONS.join(", ")}`);
	if (!(DISTRIBUTIONS as readonly string[]).includes(distribution)) {
		throw new ModelError("UNSUPPORTED_MODEL", `"${distribution}" is not a teaching model; the models are ${DISTRIBUTIONS.join(", ")}`);
	}
	const description = MODELS.find((m) => m.distribution === distribution)!;
	const family = FAMILIES[description.distribution];
	const parameters = readParameters(description, request.parameters);
	family.check(parameters);
	if (typeof sampleSize !== "number" || !Number.isInteger(sampleSize) || sampleSize < SAMPLE_SIZE.min || sampleSize > SAMPLE_SIZE.max) {
		refuse(`model.sampleSize must be an integer from ${SAMPLE_SIZE.min} to ${SAMPLE_SIZE.max}, not ${shown(sampleSize)}`);
	}
	if (typeof seed !== "number" || !Number.isSafeInteger(seed)) refuse(`model.seed must be an integer from -${Number.MAX_SAFE_INTEGER} to ${Number.MAX_SAFE_INTEGER}, not ${shown(seed)}`);
	const x = family.grid(parameters).filter((v, i, all) => i === 0 || v > all[i - 1]!);
	const y = x.map((v) => family.at(parameters, v));
	const sample = family.sample(parameters, seededUniform(seed), sampleSize);
	if (x.length < 2 || ![...x, ...y, ...sample].every(Number.isFinite)) {
		refuse(`the ${distribution} parameters ${JSON.stringify(parameters)} are too extreme to calculate in double precision; choose values of a more ordinary scale`);
	}
	return { distribution: description.distribution, parameters, sampleSize, seed, sample, curve: { x, y } };
}

function readParameters(description: ModelDescription, value: unknown): Params {
	const names = description.parameters.map((p) => p.name);
	const takes = `the ${description.distribution} model takes parameters ${names.join(" and ")}`;
	if (!isObject(value)) refuse(`model.parameters must be an object: ${takes}`);
	const unknown = Object.keys(value).filter((key) => !names.includes(key));
	if (unknown.length) refuse(`model.parameters has unknown "${unknown[0]}": ${takes}`);
	const out: Params = {};
	for (const name of names) {
		const v = value[name];
		if (typeof v !== "number" || !Number.isFinite(v)) refuse(`model.parameters.${name} must be a finite number, not ${shown(v)}: ${takes}`);
		out[name] = v;
	}
	return out;
}

function isObject(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === "object" && !Array.isArray(value);
}

function shown(value: unknown): string {
	const json = JSON.stringify(value) ?? String(value);
	return json.length > 40 ? `${json.slice(0, 40)}...` : json;
}

const TITLES: Record<Distribution, string> = { normal: "Normal", uniform: "Uniform", exponential: "Exponential", binomial: "Binomial", poisson: "Poisson" };

/** The model's two traces: its seeded sample as a density histogram (meta.role "samples") and its formula curve (meta.role "model"). */
export function modelTraces(model: TeachingModel): PlotTrace[] {
	const description = MODELS.find((m) => m.distribution === model.distribution)!;
	const settings = description.parameters.map((p) => `${p.name} ${model.parameters[p.name]}`).join(", ");
	const samples: PlotTrace = {
		type: "histogram",
		name: `Seeded sample (n = ${model.sampleSize}, seed ${model.seed})`,
		x: model.sample,
		histnorm: "probability density",
		xbins: FAMILIES[model.distribution].bins(model.parameters, model.sample),
		opacity: 0.45,
		meta: { role: "samples" },
	};
	const curve: PlotTrace = {
		type: "scatter",
		mode: description.discrete ? "markers" : "lines",
		name: `${TITLES[model.distribution]} ${description.discrete ? "mass" : "density"} (${settings})`,
		x: model.curve.x,
		y: model.curve.y,
		meta: { role: "model" },
	};
	return [samples, curve];
}

/** What the chart of a model shows, in sentences for the reader. */
export function modelExplanation(model: TeachingModel): string[] {
	const description = MODELS.find((m) => m.distribution === model.distribution)!;
	return [
		`Teaching model, not observed data: the ${model.distribution} distribution with ${description.parameters.map((p) => `${p.name} ${model.parameters[p.name]}`).join(" and ")}.`,
		`The ${description.discrete ? "points are the" : "curve is the"} ${description.curve}.`,
		`The bars are a sample of ${model.sampleSize} drawn by code with seed ${model.seed} (${description.sampler}), shown as a density histogram so it can be compared with the ${description.discrete ? "masses" : "curve"}.`,
	];
}
